import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Spin } from 'antd'
import { Icon } from '@iconify/react'

import {
    findDefaultAvatarOptionBySrc,
    getDefaultAvatarsForGender,
    getRandomAvatar,
} from '../../../assets/clients/avatar'
import { resolveProfilePictureUrl } from '../../../components/MentionEditor/MentionEditorMentionBlot'
import { showActionNotification } from '../../../components/notification'
import {
    uploadClientImageToS3,
    validateClientProfileImage,
} from '../utils/clientImageUpload'

import styles from './ClientProfilePictureField.module.css'

const EMPTY_PROFILE = {
    mode: 'default',
    previewUrl: null,
    defaultAvatarId: null,
    uploadedUrl: null,
    touched: false,
}

const ClientProfilePictureField = ({
    value = EMPTY_PROFILE,
    onChange,
    gender,
    disabled = false,
    dispatch,
    /** Client's saved photo when editing — kept visible until a new avatar is chosen */
    existingProfilePicture,
}) => {
    const fileInputRef = useRef(null)
    const [uploading, setUploading] = React.useState(false)

    const openFilePicker = useCallback(() => {
        if (disabled || uploading) return
        fileInputRef.current?.click()
    }, [disabled, uploading])

    const avatarOptions = useMemo(
        () => getDefaultAvatarsForGender(gender),
        [gender]
    )

    const resolvedExisting = useMemo(
        () => resolveProfilePictureUrl(existingProfilePicture),
        [existingProfilePicture]
    )

    const customPhotoUrl = useMemo(() => {
        const fromState = value?.uploadedUrl || value?.previewUrl
        if (fromState && !findDefaultAvatarOptionBySrc(fromState)) {
            return fromState
        }
        if (resolvedExisting && !findDefaultAvatarOptionBySrc(resolvedExisting)) {
            return resolvedExisting
        }
        return null
    }, [value?.uploadedUrl, value?.previewUrl, resolvedExisting])

    const previewUrl = useMemo(() => {
        if (value?.previewUrl) return value.previewUrl
        if (value?.uploadedUrl) return value.uploadedUrl
        if (value?.mode === 'default' && value?.defaultAvatarId) {
            const opt = avatarOptions.find(
                (o) => o.id === value.defaultAvatarId
            )
            if (opt?.src) return opt.src
        }
        if (resolvedExisting) return resolvedExisting
        return getRandomAvatar({ gender, seed: 'client-modal' }) || null
    }, [value, avatarOptions, gender, resolvedExisting])

    const setProfile = useCallback(
        (patch, options = {}) => {
            const markTouched = options.touched ?? true
            onChange?.({
                ...EMPTY_PROFILE,
                ...value,
                ...patch,
                touched: markTouched,
            })
        },
        [onChange, value]
    )

    const handleSelectDefault = useCallback(
        (option) => {
            if (disabled || uploading) return
            setProfile({
                mode: 'default',
                defaultAvatarId: option.id,
                previewUrl: option.src,
                uploadedUrl: null,
            })
        },
        [disabled, uploading, setProfile]
    )

    const handleTabChange = useCallback(
        (mode) => {
            if (disabled || uploading || value?.mode === mode) return
            if (mode === 'default') {
                if (customPhotoUrl) {
                    setProfile(
                        {
                            mode: 'default',
                            defaultAvatarId: null,
                            previewUrl: customPhotoUrl,
                            uploadedUrl: value?.uploadedUrl || customPhotoUrl,
                        },
                        { touched: value?.touched ?? false }
                    )
                    return
                }
                const first = avatarOptions[0]
                setProfile(
                    {
                        mode: 'default',
                        defaultAvatarId: first?.id ?? null,
                        previewUrl: first?.src ?? previewUrl,
                        uploadedUrl: null,
                    },
                    { touched: value?.touched ?? false }
                )
            } else {
                setProfile(
                    {
                        mode: 'upload',
                        defaultAvatarId: null,
                        previewUrl:
                            value?.uploadedUrl ||
                            customPhotoUrl ||
                            previewUrl,
                        uploadedUrl:
                            value?.uploadedUrl ||
                            customPhotoUrl ||
                            null,
                    },
                    { touched: value?.touched ?? false }
                )
            }
        },
        [
            disabled,
            uploading,
            value?.mode,
            value?.touched,
            value?.uploadedUrl,
            avatarOptions,
            previewUrl,
            customPhotoUrl,
            setProfile,
        ]
    )

    const handleFileChange = useCallback(
        async (event) => {
            const file = event.target.files?.[0]
            if (event.target) event.target.value = ''
            if (!file || disabled || !dispatch) return

            const validation = validateClientProfileImage(file)
            if (!validation.ok) {
                showActionNotification({
                    type: 'error',
                    message: 'Invalid image',
                    subMessage: validation.reason,
                    notificationKey: 'client-avatar-invalid',
                })
                return
            }

            const localPreview = URL.createObjectURL(file)
            setProfile({
                mode: 'upload',
                previewUrl: localPreview,
                defaultAvatarId: null,
            })

            setUploading(true)
            try {
                const publicLink = await uploadClientImageToS3(file, dispatch)
                setProfile({
                    mode: 'upload',
                    previewUrl: publicLink,
                    uploadedUrl: publicLink,
                    defaultAvatarId: null,
                })
            } catch (err) {
                showActionNotification({
                    type: 'error',
                    message: 'Upload failed',
                    subMessage: err?.message || 'Please try again in a moment.',
                    notificationKey: 'client-avatar-upload-failed',
                })
                setProfile({
                    mode: 'upload',
                    previewUrl: value?.uploadedUrl || null,
                    uploadedUrl: value?.uploadedUrl || null,
                })
            } finally {
                setUploading(false)
            }
        },
        [disabled, dispatch, setProfile, value?.uploadedUrl]
    )

    useEffect(() => {
        if (value?.touched) return
        if (value?.previewUrl || value?.uploadedUrl) return
        if (resolvedExisting) return

        const randomSrc = getRandomAvatar({ gender, seed: 'client-modal' })
        const match = avatarOptions.find((o) => o.src === randomSrc)
        const initial = match || avatarOptions[0]
        if (!initial) return

        onChange?.({
            mode: 'default',
            defaultAvatarId: initial.id,
            previewUrl: initial.src,
            uploadedUrl: null,
            touched: false,
        })
    }, [
        gender,
        avatarOptions,
        value?.touched,
        value?.previewUrl,
        value?.uploadedUrl,
        resolvedExisting,
        onChange,
    ])

    useEffect(() => {
        if (!resolvedExisting || value?.touched) return
        const alreadySet =
            value?.previewUrl === resolvedExisting ||
            value?.uploadedUrl === resolvedExisting
        if (alreadySet) return

        const defaultMatch = findDefaultAvatarOptionBySrc(resolvedExisting)
        if (defaultMatch) {
            onChange?.({
                mode: 'default',
                defaultAvatarId: defaultMatch.id,
                previewUrl: defaultMatch.src,
                uploadedUrl: null,
                touched: false,
            })
            return
        }

        onChange?.({
            mode: 'upload',
            previewUrl: resolvedExisting,
            uploadedUrl: resolvedExisting,
            defaultAvatarId: null,
            touched: false,
        })
    }, [
        resolvedExisting,
        value?.touched,
        value?.previewUrl,
        value?.uploadedUrl,
        onChange,
    ])

    useEffect(() => {
        if (!value?.touched || value?.mode !== 'default') return
        if (!value?.defaultAvatarId) return
        const stillValid = avatarOptions.some(
            (o) => o.id === value.defaultAvatarId
        )
        if (stillValid) return
        const first = avatarOptions[0]
        if (!first) return
        onChange?.({
            ...value,
            defaultAvatarId: first.id,
            previewUrl: first.src,
        })
    }, [gender, avatarOptions, value, onChange])

    const activeTab = value?.mode === 'upload' ? 'upload' : 'default'

    return (
        <div className={styles.profileSection}>
            <span className={styles.profileSectionLabel}>Profile picture</span>

            <div className={styles.profileHero}>
                <div className={styles.previewRing}>
                    {previewUrl ? (
                        <img
                            src={previewUrl}
                            alt=""
                            className={styles.previewImage}
                        />
                    ) : (
                        <div className={styles.previewPlaceholder}>
                            <Icon icon="mdi:account" width={28} height={28} />
                        </div>
                    )}
                    {uploading && (
                        <div className={styles.previewSpinner}>
                            <Spin size="small" />
                        </div>
                    )}
                </div>
                <div className={styles.heroCopy}>
                    <p className={styles.heroTitle}>Choose how it looks</p>
                    <p className={styles.heroHint}>
                        Pick a ready-made avatar or upload a photo (PNG, JPEG,
                        or WebP, max 5MB).
                    </p>
                </div>
            </div>

            <div className={styles.tabRow}>
                <button
                    type="button"
                    className={`${styles.tabBtn} ${
                        activeTab === 'default' ? styles.tabBtnActive : ''
                    }`}
                    onClick={() => handleTabChange('default')}
                    disabled={disabled || uploading}
                >
                    Default avatars
                </button>
                <button
                    type="button"
                    className={`${styles.tabBtn} ${
                        activeTab === 'upload' ? styles.tabBtnActive : ''
                    }`}
                    onClick={() => handleTabChange('upload')}
                    disabled={disabled || uploading}
                >
                    Upload photo
                </button>
            </div>

            {activeTab === 'default' ? (
                avatarOptions.length > 0 ? (
                    <div className={styles.avatarScroll}>
                        <div className={styles.avatarGrid}>
                            {avatarOptions.map((option) => {
                                const isActive =
                                    value?.defaultAvatarId === option.id
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={`${styles.avatarOption} ${
                                            isActive
                                                ? styles.avatarOptionActive
                                                : ''
                                        }`}
                                        onClick={() =>
                                            handleSelectDefault(option)
                                        }
                                        disabled={disabled || uploading}
                                        aria-label={`Avatar ${option.id}`}
                                        aria-pressed={isActive}
                                    >
                                        <img src={option.src} alt="" />
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                ) : (
                    <p className={styles.emptyGenderHint}>
                        Select a gender above to see matching avatars, or choose
                        Other to browse all.
                    </p>
                )
            ) : (
                <div className={styles.uploadPanel}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className={`client-profile-file-input ${styles.hiddenFileInput}`}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        disabled={disabled || uploading}
                        tabIndex={-1}
                        aria-hidden="true"
                    />
                    <div
                        role="button"
                        tabIndex={disabled || uploading ? -1 : 0}
                        className={`${styles.uploadDropzone} ${
                            disabled || uploading
                                ? styles.uploadDropzoneDisabled
                                : ''
                        }`}
                        onClick={openFilePicker}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                openFilePicker()
                            }
                        }}
                        aria-label="Upload profile photo"
                    >
                        <span className={styles.uploadIcon}>
                            <Icon icon="mdi:cloud-upload-outline" />
                        </span>
                        <p className={styles.uploadTitle}>
                            {value?.uploadedUrl
                                ? 'Tap to replace your photo'
                                : 'Click to upload'}
                        </p>
                        <p className={styles.uploadHint}>
                            PNG, JPEG, or WebP · up to 5MB
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ClientProfilePictureField
export { EMPTY_PROFILE as EMPTY_CLIENT_PROFILE }
